import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const AUTO_CLICK_PRIORITY_LAYER_ID = 'auto';

/**
 * Right-slide drawer for Data Layers. Matches charger module DataLayersDrawer style.
 * Props: isOpen, onClose, groups (array of { title, items: [{ id, label, description, checked, onChange, icon, iconClass }] }).
 */
const RegulatorDataLayersDrawer = ({
  isOpen,
  onClose,
  groups = [],
  clickPriorityOptions = [],
  clickPriorityValue = AUTO_CLICK_PRIORITY_LAYER_ID,
  onClickPriorityChange,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-black/30 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Data Layers"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Data Layers</h3>
            <p className="text-sm text-gray-500">Toggle supplemental map overlays by category.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close data layers"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 px-6 py-4 overflow-y-auto space-y-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <label
              htmlFor="regulator-click-priority"
              className="block text-xs font-semibold text-gray-600 uppercase tracking-wide"
            >
              Click Priority
            </label>
            <p className="mt-1 text-xs text-gray-500">
              Choose which visible layer gets click priority when overlapping.
            </p>
            <select
              id="regulator-click-priority"
              value={clickPriorityValue}
              onChange={(e) => onClickPriorityChange?.(e.target.value)}
              disabled={clickPriorityOptions.length <= 1}
              className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value={AUTO_CLICK_PRIORITY_LAYER_ID}>Auto</option>
              {clickPriorityOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          {groups.map((group) => (
            <div key={group.title}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {group.title}
              </h4>
              <div className="space-y-3">
                {group.items.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-lg border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30 transition"
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => item.onChange(!item.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        aria-label={item.label}
                      />
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {item.icon && (
                          <span
                            className={`mt-0.5 shrink-0 ${item.iconClass || 'text-blue-600'}`}
                            aria-hidden="true"
                          >
                            <item.icon className="w-5 h-5" />
                          </span>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800">{item.label}</div>
                          {item.description && (
                            <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                          )}
                        </div>
                      </div>
                    </label>
                    {item.numericFilter && (
                      <div className="mt-3 ml-7">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-gray-600">
                            {item.numericFilter.label}
                          </label>
                          <span className="text-xs font-semibold text-blue-700">
                            {item.numericFilter.value}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={item.numericFilter.min ?? 0}
                          max={item.numericFilter.max ?? 100}
                          step={item.numericFilter.step ?? 1}
                          value={item.numericFilter.value}
                          onChange={(e) => item.numericFilter.onChange(e.target.value)}
                          className="w-56 accent-blue-600"
                        />
                        <div className="mt-2 flex items-center gap-2">
                          {(item.numericFilter.quickValues || []).map((val) => (
                            <button
                              key={`${item.id}-quick-${val}`}
                              type="button"
                              onClick={() => item.numericFilter.onChange(val)}
                              className="px-2 py-0.5 text-[11px] rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default RegulatorDataLayersDrawer;
